import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
// DTC-L: route OptiBay verify_at write through clock.
import { clock } from '@/utils/clock';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, ArrowRight, Plus, Trash2, ShieldCheck, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import {
  useSessionVerifications,
  useAddPostVerification,
  useUpdatePostVerification,
  useRemovePostVerification,
} from '@/hooks/useOptiBayPostVerification';
import { useSessionRepairs } from '@/hooks/useOptiBayRepair';
import type { OptiBaySession, OptiBaySessionVerification, OptiBayVerificationResult } from '@/types/optibay';
import { cn } from '@/lib/utils';

interface OptiBayVerifyStepProps {
  session: OptiBaySession;
  onNext: () => void;
  onBack: () => void;
}

const RESULT_CONFIG: Record<OptiBayVerificationResult, { label: string; icon: typeof CheckCircle2; color: string }> = {
  pending: { label: 'Pending', icon: AlertCircle, color: 'text-muted-foreground' },
  passed: { label: 'Passed', icon: CheckCircle2, color: 'text-green-600' },
  failed: { label: 'Failed', icon: XCircle, color: 'text-destructive' },
  inconclusive: { label: 'Inconclusive', icon: AlertCircle, color: 'text-amber-500' },
};

const VERIFICATION_METHODS = [
  'Road Test',
  'Visual Inspection',
  'Scan Tool Recheck',
  'Measurement Recheck',
  'Functional Test',
  'Customer Confirmation',
  'Other',
];

function OutcomeToggle({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean | null;
  onChange: (val: boolean) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] text-muted-foreground w-28">{label}</span>
      <button
        type="button"
        onClick={() => onChange(true)}
        className={cn(
          'text-[10px] px-2 py-0.5 rounded border transition-colors',
          value === true
            ? 'border-green-500 bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-400'
            : 'border-border text-muted-foreground hover:bg-accent/50'
        )}
      >
        Yes
      </button>
      <button
        type="button"
        onClick={() => onChange(false)}
        className={cn(
          'text-[10px] px-2 py-0.5 rounded border transition-colors',
          value === false
            ? 'border-destructive bg-destructive/10 text-destructive'
            : 'border-border text-muted-foreground hover:bg-accent/50'
        )}
      >
        No
      </button>
    </div>
  );
}

export function OptiBayVerifyStep({ session, onNext, onBack }: OptiBayVerifyStepProps) {
  const { data: verifications = [] } = useSessionVerifications(session.id);
  const { data: repairs = [] } = useSessionRepairs(session.id);
  const addVerification = useAddPostVerification();
  const updateVerification = useUpdatePostVerification();
  const removeVerification = useRemovePostVerification();

  const [showForm, setShowForm] = useState(false);
  const [method, setMethod] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedRepairId, setSelectedRepairId] = useState('');

  const handleAdd = async () => {
    if (!method.trim()) return;
    await addVerification.mutateAsync({
      sessionId: session.id,
      repairId: selectedRepairId || undefined,
      verificationMethod: method.trim(),
      notes: notes.trim() || undefined,
    });
    setMethod('');
    setNotes('');
    setSelectedRepairId('');
    setShowForm(false);
  };

  const handleSetResult = async (v: OptiBaySessionVerification, result: OptiBayVerificationResult) => {
    await updateVerification.mutateAsync({
      id: v.id,
      updates: {
        result,
        verified_at: result !== 'pending' ? clock.now().toISOString() : null,
      },
    });
  };

  const handleToggle = async (v: OptiBaySessionVerification, field: 'repair_verified' | 'symptom_resolved' | 'comeback_flag', value: boolean) => {
    await updateVerification.mutateAsync({
      id: v.id,
      updates: { [field]: value } as any,
    });
  };

  const allResolved = verifications.length > 0 && verifications.every(v => v.result !== 'pending');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">Post-Repair Verification</h4>
        <Badge variant="secondary" className="text-xs">
          {verifications.filter(v => v.result !== 'pending').length}/{verifications.length} verified
        </Badge>
      </div>

      <div className="rounded-md border border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/30 p-3 text-xs text-green-800 dark:text-green-200">
        <ShieldCheck className="h-3.5 w-3.5 inline mr-1.5" />
        Verify that each repair resolved the original symptom. This step confirms your diagnostic was accurate.
      </div>

      {verifications.length === 0 && !showForm ? (
        <p className="text-xs text-muted-foreground text-center py-4">
          No verification checks yet. Add verification methods to confirm repairs.
        </p>
      ) : (
        <div className="space-y-2">
          {verifications.map(v => {
            const resultCfg = RESULT_CONFIG[v.result as OptiBayVerificationResult] || RESULT_CONFIG.pending;
            const Icon = resultCfg.icon;
            const linkedRepair = repairs.find(r => r.id === v.repair_id);
            return (
              <div key={v.id} className="border rounded-lg p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <Icon className={cn('h-3.5 w-3.5 shrink-0', resultCfg.color)} />
                    <span className="text-xs font-semibold">{v.verification_method}</span>
                    <Badge variant="outline" className="text-[10px]">{resultCfg.label}</Badge>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0 text-destructive/70 hover:text-destructive"
                    onClick={() => removeVerification.mutate({ id: v.id, sessionId: session.id })}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
                {linkedRepair && (
                  <p className="text-[10px] text-muted-foreground pl-5">
                    Repair: {linkedRepair.repair_description}
                  </p>
                )}
                {v.notes && <p className="text-xs text-muted-foreground pl-5">{v.notes}</p>}

                {v.result === 'pending' && (
                  <div className="flex gap-1.5 pl-5">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-6 text-[10px] text-green-600"
                      onClick={() => handleSetResult(v, 'passed')}
                    >
                      <CheckCircle2 className="h-3 w-3 mr-0.5" />
                      Pass
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-6 text-[10px] text-destructive"
                      onClick={() => handleSetResult(v, 'failed')}
                    >
                      <XCircle className="h-3 w-3 mr-0.5" />
                      Fail
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-6 text-[10px]"
                      onClick={() => handleSetResult(v, 'inconclusive')}
                    >
                      Inconclusive
                    </Button>
                  </div>
                )}

                {/* Outcome detail toggles — show after result is set */}
                {v.result !== 'pending' && (
                  <div className="space-y-1.5 pl-5 pt-1 border-t border-dashed">
                    <OutcomeToggle
                      label="Repair verified?"
                      value={(v as any).repair_verified ?? null}
                      onChange={(val) => handleToggle(v, 'repair_verified', val)}
                    />
                    <OutcomeToggle
                      label="Symptom resolved?"
                      value={(v as any).symptom_resolved ?? null}
                      onChange={(val) => handleToggle(v, 'symptom_resolved', val)}
                    />
                    <OutcomeToggle
                      label="Comeback risk?"
                      value={(v as any).comeback_flag ?? null}
                      onChange={(val) => handleToggle(v, 'comeback_flag', val)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showForm ? (
        <div className="border rounded-lg p-3 space-y-2">
          <div>
            <label className="text-[10px] text-muted-foreground mb-1 block">Verification Method</label>
            <div className="flex gap-1 flex-wrap">
              {VERIFICATION_METHODS.map(m => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMethod(method === m ? '' : m)}
                  className={cn(
                    'text-[10px] px-2 py-0.5 rounded-full border transition-colors',
                    method === m
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'hover:bg-accent/50 text-muted-foreground',
                  )}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
          {repairs.length > 0 && (
            <div>
              <label className="text-[10px] text-muted-foreground mb-1 block">Link to repair (optional)</label>
              <div className="flex gap-1 flex-wrap">
                {repairs.map(r => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setSelectedRepairId(selectedRepairId === r.id ? '' : r.id)}
                    className={cn(
                      'text-[10px] px-2 py-0.5 rounded-full border transition-colors truncate max-w-[200px]',
                      selectedRepairId === r.id
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'hover:bg-accent/50 text-muted-foreground',
                    )}
                  >
                    {r.repair_description.slice(0, 40)}
                  </button>
                ))}
              </div>
            </div>
          )}
          <Textarea
            placeholder="Notes (optional)…"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            className="text-xs min-h-[50px]"
          />
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button size="sm" onClick={handleAdd} disabled={!method.trim() || addVerification.isPending}>
              Add Verification
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="outline"
          size="sm"
          className="w-full text-xs"
          onClick={() => setShowForm(true)}
        >
          <Plus className="h-3 w-3 mr-1" />
          Add Verification Check
        </Button>
      )}

      <div className="flex justify-between pt-2">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-3.5 w-3.5 mr-1" />
          Back
        </Button>
        <Button size="sm" onClick={onNext} disabled={!allResolved}>
          Continue to Training
          <ArrowRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      </div>
    </div>
  );
}
